import { useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Plus, X, UserPlus, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API } from '../../config';

const ROLES = ['admin', 'manager', 'member', 'viewer'];

export default function DevUsers() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTenant, setFilterTenant] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterAuth, setFilterAuth] = useState('all');
  const [showDemo, setShowDemo] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', tenant_id: '', role: 'member', is_developer: false });
  const [addingUser, setAddingUser] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    Promise.all([fetchUsers(), fetchTenants()]).finally(() => setLoading(false));
  }, [showDemo]);

  const fetchUsers = async () => {
    try { const res = await axios.get(`${API}/dev/users`, { params: { include_demo: showDemo }, withCredentials: true }); setUsers(res.data); } catch (err) { console.error(err); }
  };
  const fetchTenants = async () => {
    try { const res = await axios.get(`${API}/dev/tenants`, { params: { include_demo: showDemo }, withCredentials: true }); setTenants(res.data); } catch (err) { console.error(err); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/dev/users/${userId}`, { role: newRole }, { withCredentials: true });
      toast.success('Role updated');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update role'); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.tenant_id) { toast.error('Select a tenant'); return; }
    setAddingUser(true);
    try {
      await axios.post(`${API}/dev/users`, newUser, { withCredentials: true });
      toast.success(`User "${newUser.name}" created`);
      setNewUser({ name: '', email: '', password: '', tenant_id: '', role: 'member' });
      setShowAddUser(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally { setAddingUser(false); }
  };

  const tenantName = (tenantId) => tenants.find((t) => t.tenant_id === tenantId)?.name || tenantId;

  const deleteUser = async (u) => {
    setConfirmDelete(u);
  };

  const confirmDeleteUser = async () => {
    const u = confirmDelete;
    if (!u) return;
    setConfirmDelete(null);
    try {
      await axios.delete(`${API}/dev/users/${u.user_id}`, { withCredentials: true });
      toast.success(`${u.email} deleted`);
      fetchUsers();
    } catch (err) {
      console.error('[DevUsers] Delete failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const filtered = users.filter((u) => {
    if (filterTenant !== 'all' && u.tenant_id !== filterTenant) return false;
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (filterAuth !== 'all' && u.auth_provider !== filterAuth) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!u.name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" /></div>;
  }

  return (
    <div className="p-8" data-testid="dev-users">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Users Directory</h1>
          <p className="text-sm text-slate-400 mt-1">{users.length} total users{!showDemo ? ' (demo hidden)' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowDemo(!showDemo)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${showDemo ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600'}`}
            data-testid="toggle-demo-users"
            title={showDemo ? 'Hide demo users' : 'Show demo users'}
          >
            {showDemo ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {showDemo ? 'Demo Visible' : 'Demo Hidden'}
          </button>
          <Button onClick={() => setShowAddUser(!showAddUser)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-sm" data-testid="add-user-btn">
            <UserPlus className="w-4 h-4 mr-1.5" />
            New User
          </Button>
        </div>
      </div>

      {/* Add User Form */}
      {showAddUser && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 mb-6" data-testid="add-user-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Create User</h3>
            <button onClick={() => setShowAddUser(false)} className="text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
          </div>
          <form onSubmit={handleAddUser} className="space-y-3">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Full Name *</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="John Doe" className="bg-slate-800 border-slate-700 text-slate-200 h-9" required data-testid="new-user-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Email *</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@example.com" className="bg-slate-800 border-slate-700 text-slate-200 h-9" required data-testid="new-user-email" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Password *</Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Min 6 chars" className="bg-slate-800 border-slate-700 text-slate-200 h-9" required data-testid="new-user-password" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Tenant *</Label>
                <Select value={newUser.tenant_id} onValueChange={(val) => setNewUser({ ...newUser, tenant_id: val })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300 h-9" data-testid="new-user-tenant">
                    <SelectValue placeholder="Select tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Role</Label>
                <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                  <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-slate-300 h-9" data-testid="new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1" />
              <Button type="submit" disabled={addingUser} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-sm" data-testid="create-user-submit">
                {addingUser ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <Input placeholder="Search name or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-slate-900 border-slate-700 text-slate-200 placeholder:text-slate-600 h-9" data-testid="dev-users-search" />
        </div>
        <Select value={filterTenant} onValueChange={setFilterTenant}>
          <SelectTrigger className="w-48 bg-slate-900 border-slate-700 text-slate-300 h-9" data-testid="dev-users-tenant-filter"><SelectValue placeholder="All Tenants" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tenants</SelectItem>
            {tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-36 bg-slate-900 border-slate-700 text-slate-300 h-9" data-testid="dev-users-role-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAuth} onValueChange={setFilterAuth}>
          <SelectTrigger className="w-32 bg-slate-900 border-slate-700 text-slate-300 h-9" data-testid="dev-users-auth-filter"><SelectValue placeholder="Auth" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Auth</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="google">Google</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-5 py-3">User</th>
              <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-3 py-3">Organization</th>
              <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-3 py-3">Role</th>
              <th className="text-center text-xs text-slate-500 uppercase tracking-wider px-3 py-3">Auth</th>
              <th className="text-right text-xs text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {filtered.map((u) => (
              <tr key={u.user_id} className="hover:bg-slate-800/40 transition-colors" data-testid={`dev-user-row-${u.user_id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2.5">
                    {u.picture ? <img src={u.picture} alt="" className="w-7 h-7 rounded-full" /> : <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-xs font-medium text-slate-300">{u.name?.[0]?.toUpperCase() || '?'}</div>}
                    <div>
                      <p className="text-sm text-slate-200 flex items-center gap-1.5">
                        {u.name}
                        {u.is_demo && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-medium">DEMO</span>}
                      </p>
                      <p className="text-[10px] text-slate-500">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3"><p className="text-xs text-slate-400">{tenantName(u.tenant_id)}</p></td>
                <td className="px-3 py-3">
                  <Select value={u.role} onValueChange={(val) => changeRole(u.user_id, val)}>
                    <SelectTrigger className="w-28 h-7 text-xs bg-transparent border-slate-700 text-slate-300"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-3 text-center">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${u.auth_provider === 'google' ? 'text-blue-400 bg-blue-400/10' : 'text-slate-400 bg-slate-700'}`}>
                    {u.auth_provider === 'google' ? 'Google' : 'Email'}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  <span className="text-[10px] text-slate-600 mr-3">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</span>
                  <button
                    onClick={() => deleteUser(u)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-red-500/15 text-red-400/50 hover:text-red-400 transition-colors"
                    data-testid={`dev-user-delete-${u.user_id}`}
                    title={`Delete ${u.email}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="px-5 py-8 text-sm text-slate-500 text-center">No users match filters</p>}
      </div>

      {/* Delete Confirmation Dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="delete-confirm-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Delete User</h3>
                <p className="text-xs text-slate-400">This cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-slate-300 mb-5">
              Permanently delete <span className="font-medium text-white">{confirmDelete.email}</span>?
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" className="h-8 text-xs bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800" onClick={() => setConfirmDelete(null)} data-testid="delete-cancel-btn">Cancel</Button>
              <Button className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={confirmDeleteUser} data-testid="delete-confirm-btn">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
